<script lang="ts">

import { onMount } from "svelte";
import { fetchPublications } from "../services/publications";
import { fade } from "svelte/transition";
import { quintOut } from "svelte/easing";

// Fetch publications data from Wordpress publications category
const publicationsFetch = new Promise(async (resolve: (data: any[]) => void) => {
    let data = await fetchPublications();

    // Sort each publication category by year
    data.forEach((category) => {
        // List of publication strings and objects to be categorized by year
        let categoryItems = (category.objectList) ? category.objectList : category.list;
        
        if (categoryItems) {
            let sortedItemsByYear = [];
            categoryItems.forEach((item) => {
                let year;
                if (item.year !== null && item.year !== undefined) {
                    // Get year if year is provided (used for Thesis)
                    year = (item.year) ? item.year : "Uncategorized";
                } else {
                    // Use regex pattern to get year from publication text
                    const regEx = /(?:(?:19|20|21)[0-9]{2})/g;
                    year = (item.match(regEx)) ? item.match(regEx)[0] : "Uncategorized";
                }

                // Add to exiting year category or create a new year category
                let yearItem = sortedItemsByYear.find(o => o.year === year);
                let itemIndex = sortedItemsByYear.indexOf(yearItem);
                yearItem ??= { year: year, data: [] };
                yearItem.data.push(item);

                (itemIndex < 0) ? sortedItemsByYear.push(yearItem) : sortedItemsByYear[itemIndex] = yearItem;
            });
            // Sort by most recent year
            category.filteredList = sortedItemsByYear.sort((a,b) => parseInt(b.year) - parseInt(a.year));
        }
    });

    resolve(data);
});

let category = "Journal";
let searchTerm = "";
let publicationsData = [];

$: activeCategory = publicationsData.find(o => o.category === category);
$: filteredResults = (activeCategory) ? activeCategory.filteredList.map(list => {
    return {
        year: list.year,
        data: list.data.filter(item => 
            (item.text !== null && item.text !== undefined) ? 
                item.text.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0 :
                item.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0)
    }
}).filter(list => list.data.length > 0) : null;


onMount(async () => {
    publicationsData = await publicationsFetch;
});

</script>



<div class="top-background" style="background-image:url(images/backgrounds/publications.jpg);">
    <h1 class="page-title">Publications</h1>
    <!-- Credit to the photographer (not required but still giving it) -->
    <div class="credit">
        Photo by <a href="https://unsplash.com/@alexandrevanthuan?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText">Alexandre Van Thuan</a>
    </div>
</div>

<section id="publications">
    <!--Buttons which open the respective 'tab' based on publication type-->
    <div class="tab-wrapper">
        <button on:click={() => category = "Journal"} class:active-category={category === "Journal"}>Journals</button>
        <button on:click={() => category = "Conference"} class:active-category={category === "Conference"}>Conferences</button>
        <button on:click={() => category = "Industrial"} class:active-category={category === "Industrial"}>Industrial Reports</button>
        <button on:click={() => category = "Thesis"} class:active-category={category === "Thesis"}>Thesis</button>
    </div>
    
    {#if filteredResults}
        <div class="content">
            <div in:fade={{ easing: quintOut, duration: 500 }}>
                <div class="top-wrapper">
                    <h2 class="category-title">{activeCategory.category} Publications</h2>
                    <input bind:value={searchTerm} class="search-box" type="text" placeholder="Search for a Publication...">
                </div>
                {#each filteredResults as yearCategory}
                    <ul class="publication-category">
                        <li class="header">{yearCategory.year}</li>
                        <div>
                            {#each yearCategory.data as item}
                                {#if (item.text !== null && item.text !== undefined) && (item.link !== null && item.link !== undefined)}
                                    <li><a href="{item.link}" target="_blank">{@html item.text}</a></li>
                                {:else}
                                    <li>{@html item}</li>
                                {/if}
                            {/each}
                        </div>
                    </ul>
                {/each}
                
                <!-- If search result cant match anything -->
                {#if filteredResults.length < 1}
                    <div class="no-results-container">
                        <img src="images/icons/no-results.png" alt="no results">
                        <h3>Couldn't find anything for "{searchTerm}"</h3>
                    </div>
                {/if}
            </div>
        </div>
    {/if}
</section>



<style lang="sass">

@import "../consts.sass"
@include page-header()
@include search-box()
@include no-search-results-text()

// Category tabs and tab buttons
.tab-wrapper
    display: flex
    flex-direction: row
    justify-content: center
    align-items: center
    width: 100%
    overflow: hidden
    background-color: #f4f4f4
    padding: 0 2em
    box-sizing: border-box
    
    @media only screen and (min-width: 1200px) 
        padding: 0 10vw

    button
        flex-grow: 1
        padding: 0.8em 0.5em
        margin: 1em 0
        box-sizing: border-box
        outline: none
        border: none
        border-radius: 0
        color: $color_ink
        font-size: 1rem
        border-radius: 5px
        transition: background-color 0.2s ease, color 0.2s ease
        -webkit-transition: background-color 0.2s ease, color 0.2s ease

        &.active-category
            background-color: $color_secondary
            box-shadow: 0px 2px 15px rgba(0,0,0,0.1)
            color: white

// Publications page
.content
    padding: 2em
    box-sizing: border-box

    @media only screen and (min-width: 1200px) 
        padding-right: 10vw
        padding-left: 10vw

    // Category name and search bar
    .top-wrapper
        width: 100%
        display: flex
        flex-direction: column
        justify-content: center
        row-gap: 1em
        margin-bottom: 2em

        .category-title
            font-size: 2.5rem
                color: $color_ink
    
    // Publications lists
    ul.publication-category
        list-style-type: none
        padding: 0
        margin: 0
        margin-bottom: 3rem
        overflow: hidden
        border-radius: 0.2rem

        li.header
            font-size: 150%
            text-align: center
            background-color: #2F5D7C
            color: white

        li
            background-color: #f4f4f4
            border-bottom: 1px solid #bbb
            padding: 0.9rem
            box-sizing: border-box
            text-decoration: none
            font-size: 0.8rem
            color: black
            display: block

            a
                color: #2f5d7c !important

</style>